import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../Store";
import enquiryFormService from "services/enquiryFormService";
import {
  IBulkEnquiryProduct,
  IBulkEnquiryFormState,
  IEnquiryPayload,
} from "Interfaces/interface.enquirypayload";
import toast from "react-hot-toast";
import { extractErrorMessage } from "utils/ErrorHandlers";

const initialState: IBulkEnquiryFormState = {
  status: "idle",
  error: null,
  successMessage: null,
  isDialogueOpen: false,
  quantities: {},
  products: [],
};

export const submitEnquiryForm = createAsyncThunk<
  any,
  IEnquiryPayload,
  { rejectValue: string }
>("enquiry/submitEnquiryForm", async (payload, { rejectWithValue }) => {
  try {
    const response = await enquiryFormService.submitEnquiry(payload);
    return response;
  } catch (error: any) {
    toast.error(extractErrorMessage(error));
    return rejectWithValue(error.message || "Failed to submit enquiry");
  }
});

const enquiryFormSlice = createSlice({
  name: "enquiry",
  initialState,
  reducers: {
    openEnquiryDialogue: (
      state,
      action: PayloadAction<{
        products: IBulkEnquiryProduct[];
        quantities: { [key: string]: number };
      }>
    ) => {
      state.isDialogueOpen = true;
      state.products = action.payload.products;
      state.quantities = action.payload.quantities;
    },
    closeEnquiryDialogue: (state) => {
      state.isDialogueOpen = false;
      state.products = [];
      state.quantities = {};
    },
    resetEnquiryStatus: (state) => {
      state.status = "idle";
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitEnquiryForm.pending, (state) => {
        state.status = "loading";
      })
      .addCase(submitEnquiryForm.fulfilled, (state) => {
        state.status = "succeeded";
        state.successMessage = "Enquiry submitted successfully!";
      })
      .addCase(submitEnquiryForm.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload ?? "Enquiry submission failed.";
      });
  },
});

export const { openEnquiryDialogue, closeEnquiryDialogue, resetEnquiryStatus } =
  enquiryFormSlice.actions;
export default enquiryFormSlice.reducer;

// Selectors
export const selectEnquiryStatus = (state: RootState) => state.enquiry.status;
export const selectEnquiryError = (state: RootState) => state.enquiry.error;
export const selectEnquirySuccess = (state: RootState) =>
  state.enquiry.successMessage;
